import {Inject, Injectable} from "@angular/core";
import {JwtHttpClientService} from "../http/jwt-http-client.service";
import {catchError, EMPTY, first, timer} from "rxjs";
import {AuthService} from "../auth/auth.service";
import {ConfigurationServiceConstants} from "../configuration/configuration-service-constants";
import {EntityData} from "../resource/entity/entity.data";
import {DataClientError} from "../error/data-client-error";

export class LogMessage {
    constructor(public date: Date, public user: string, public message?: string, public context?: any, public error?: any) {

        // Convert an EntityData object to a simple object to avoid circular references when calling JSON.stringify
        if (this.context instanceof EntityData) {
            this.context = JSON.parse((<EntityData<any>>context).toJSONString());
        }
    }
}

/**
 * Allow the client to log messages in the server
 */
@Injectable()
export class ServerLoggingService {
    /**
     * Static URL prefix for all server side calls
     * @type {string}
     */
    private static readonly URL_PREFIX = '/dataserver/logging/';

    /**
     * Interval when there are connectivity issues
     */
    private static readonly DELAY_ERROR = 10000;

    /**
     * Poll interval when draining the queue
     */
    private static readonly DELAY_SHORT = 100;

    /**
     * Indicates that the polling service is running
     */
    private polling: boolean;

    private pendingLogMessages: LogMessage[] = [];

    /**
     *
     * @param serverHttp Server HTTP Service
     * @param authService Auth Service
     * @param application Application name used for context lookup
     */
    constructor(private serverHttp: JwtHttpClientService,
                private authService: AuthService,
                @Inject(ConfigurationServiceConstants.APPLICATION_KEY) private application: string) {
    }

    log(message?: string, context?: any, error?: any): void {
        this.pendingLogMessages.push(new LogMessage(new Date(), this.authService.getLoggedInUserName(), message, context, error));
        if (!this.polling) {
            this.initNextPoll(ServerLoggingService.DELAY_SHORT);
        }
    }

    /**
     * Initialises the poll of the queue to fire at the specified delay. Only a single poll is performed
     * to ensure all operations are completed before setting up the next poll.
     * @param {number} delayMs
     */
    private initNextPoll(delayMs: number): void {
        this.polling = true;
        timer(delayMs).pipe(first()).subscribe(() => this.processMessage());
    }

    private processMessage(): void {
        if (this.pendingLogMessages.length == 0) {
            this.polling = false;
            return;
        }
        const logMessage: LogMessage = this.pendingLogMessages[0];
        let logJson: string;
        try {
            logJson = JSON.stringify(logMessage, null, "    ");
        } catch (error) {
            logMessage.context = "Cannot convert context to json";
            try {
                logJson = JSON.stringify(logMessage, null, "    ");
            } catch (error) {
                console.log("Cannot convert message to json");
                console.error(error);
                this.initNextPoll(ServerLoggingService.DELAY_ERROR);
                return;
            }
        }
        this.serverHttp.post(ServerLoggingService.URL_PREFIX, logJson).pipe(
            catchError((error: DataClientError) => {
                console.log("Posting log message failed", error.message);
                this.initNextPoll(ServerLoggingService.DELAY_ERROR);
                return EMPTY;
            })
        ).subscribe(() => {
            this.pendingLogMessages.shift();
            this.initNextPoll(ServerLoggingService.DELAY_SHORT);
        });
    }
}
